import { useEffect, useState } from 'react';

const REQUETE_ETROITE = '(max-width: 1023.98px)';
/** Même clé que `@artosera/ui` (`FairModeToggle`) : la préférence mémorisée. */
const CLE_MODE_FOIRE = 'artosera-fair-mode';
/** Même clé que `@artosera/ui` (`ThemeToggle`) : le thème choisi explicitement. */
const CLE_THEME = 'artosera-theme';

/** Vrai sous 1 024 px : la mise en page foire s'impose, sans préférence. */
export function useEcranFoireImpose(): boolean {
  const [etroit, setEtroit] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(REQUETE_ETROITE).matches,
  );
  useEffect(() => {
    const media = window.matchMedia(REQUETE_ETROITE);
    const ecouteur = (event: MediaQueryListEvent): void => setEtroit(event.matches);
    media.addEventListener('change', ecouteur);
    return () => media.removeEventListener('change', ecouteur);
  }, []);
  return etroit;
}

function preferenceMemorisee(): boolean {
  try {
    return window.localStorage.getItem(CLE_MODE_FOIRE) === '1';
  } catch {
    return false;
  }
}

function themeMemorise(): 'light' | 'dark' | null {
  try {
    const theme = window.localStorage.getItem(CLE_THEME);
    return theme === 'light' || theme === 'dark' ? theme : null;
  } catch {
    return null;
  }
}

/** Vrai tant que le sombre posé sur `<html>` est le DÉFAUT du mode foire, pas un choix. */
let sombreImposeParLaFoire = false;

/**
 * Thème sombre par défaut du mode foire (2.6, D5 : « le thème sombre par
 * défaut »). N'écrase jamais un choix explicite : si la personne a déjà
 * touché `ThemeToggle`, sa préférence valide est restaurée sur `<html>`.
 *
 * Un défaut n'est pas un choix : il n'est PAS écrit dans `localStorage`
 * (il y restait, et le sombre survivait au mode foire, jusque sur grand
 * écran). `ThemeToggle` part du thème posé sur `<html>` et suit ses
 * changements ; il ne mémorise que ce que la personne choisit.
 */
export function appliquerThemeSombreParDefaut(): void {
  const memorise = themeMemorise();
  if (memorise) {
    document.documentElement.dataset.theme = memorise;
    return;
  }
  sombreImposeParLaFoire = true;
  document.documentElement.dataset.theme = 'dark';
}

/**
 * Sans choix explicite, la régie est claire : elle ne suit pas le réglage du
 * système (Halo clair par défaut, retour client du 21 octobre 2026). Le thème
 * est posé sur `<html>` plutôt que laissé absent, ce qui rendait `tokens.css`
 * sensible à `prefers-color-scheme`.
 */
function appliquerThemeClairParDefaut(): void {
  document.documentElement.dataset.theme = 'light';
}

/** En quittant le mode foire, un sombre imposé par la foire repasse au clair par défaut. */
function relacherThemeDeLaFoire(): void {
  if (!sombreImposeParLaFoire) return;
  sombreImposeParLaFoire = false;
  if (themeMemorise()) return;
  appliquerThemeClairParDefaut();
}

/**
 * Appliqué avant le montage de React (`main.tsx`), pour que `ThemeToggle`
 * parte du bon thème dès son premier rendu au lieu de l'appliquer un instant
 * après (l'icône afficherait sinon « clair » le temps d'un montage sur une
 * tablette en dessous de 1024 px, mission S3-i point 9).
 */
export function themeSombreSiFoireAuDemarrage(): void {
  // Sur mobile, ThemeToggle ne monte qu'à l'ouverture du menu compte.
  // Restaurer ici évite de dépendre de ce montage pour appliquer le choix.
  const memorise = themeMemorise();
  if (memorise) {
    document.documentElement.dataset.theme = memorise;
    return;
  }
  const etroit = window.matchMedia(REQUETE_ETROITE).matches;
  if (etroit || preferenceMemorisee()) {
    appliquerThemeSombreParDefaut();
    return;
  }
  appliquerThemeClairParDefaut();
}

/**
 * Sous 1024 px, le mode foire s'impose (2.6, D5) même sans bascule explicite ;
 * au-dessus, l'affichage retombe sur la préférence mémorisée par
 * `FairModeToggle`. Les deux mécanismes écrivent le même attribut
 * (`data-layout` sur `<html>`) mais jamais en même temps : celui-ci n'agit
 * qu'aux franchissements du seuil des 1024 px, `FairModeToggle` qu'aux clics.
 *
 * Un `MutationObserver` sur `data-layout` applique le thème sombre par défaut
 * dès que le mode foire s'active, qu'il vienne d'ici ou d'un clic sur
 * `FairModeToggle` : c'est la seule façon d'observer ce second cas sans
 * modifier ce composant scellé de `@artosera/ui`.
 */
export function useAutoFairLayout(): void {
  useEffect(() => {
    const media = window.matchMedia(REQUETE_ETROITE);

    const appliquer = (etroit: boolean): void => {
      if (etroit) {
        document.documentElement.dataset.layout = 'fair';
      } else if (preferenceMemorisee()) {
        document.documentElement.dataset.layout = 'fair';
      } else {
        delete document.documentElement.dataset.layout;
      }
    };

    appliquer(media.matches);
    const ecouteur = (event: MediaQueryListEvent): void => appliquer(event.matches);
    media.addEventListener('change', ecouteur);

    const html = document.documentElement;
    const surModeFoire = (): void => {
      // Sous le seuil, le mode foire s'impose : `FairModeToggle` retire
      // `data-layout` en se montant avec la préférence éteinte (désormais à
      // l'ouverture de la modale de profil), il ne doit pas la défaire.
      if (media.matches && html.dataset.layout !== 'fair') {
        html.dataset.layout = 'fair';
        return;
      }
      if (html.dataset.layout === 'fair') appliquerThemeSombreParDefaut();
      else relacherThemeDeLaFoire();
    };
    surModeFoire();
    const observateur = new MutationObserver(surModeFoire);
    observateur.observe(html, { attributes: true, attributeFilter: ['data-layout'] });

    return () => {
      media.removeEventListener('change', ecouteur);
      observateur.disconnect();
    };
  }, []);
}
